import React, { useState } from 'react';
import Header from '../components/Header';
import BottomNav from '../components/BottomNav';
import { useAuth } from '../context/AuthContext';
import { Plus, Trash2, Save, X } from 'lucide-react';

export default function SmartFileEditor() {
  const { user } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [newItem, setNewItem] = useState('');
  
  // Для groups
  const [newGroup, setNewGroup] = useState({
    name: '',
    theoryDays: [],
    theoryDuration: 1.5,
    theoryTime: '18:00'
  });
  const [selectedDay, setSelectedDay] = useState('');

  // Доступ только для staff
  if (user?.status !== 'staff') {
    return (
      <div className="min-h-screen bg-[#1b1b1b] text-[#dedede] flex items-center justify-center">
        <p className="text-xl text-red-500">Доступ запрещён</p>
      </div>
    );
  }

  // Конфигурация файлов
  const files = [
    { 
      name: 'hours.json', 
      label: 'Часы обучения', 
      path: 'hours.json',
      type: 'object',
      schema: { theory: 'число', practice: 'число' }
    },
    { 
      name: 'locations.json', 
      label: 'Локации', 
      path: 'locations.json',
      type: 'array',
      itemLabel: 'локацию',
      sortType: 'none'
    },
    { 
      name: 'times.json', 
      label: 'Время занятий', 
      path: '../times.json',
      type: 'array',
      itemLabel: 'время',
      sortType: 'time'
    },
    { 
      name: 'groups.json', 
      label: 'Группы', 
      path: '../groups.json',
      type: 'groups'  // специальный тип
    },
    { 
      name: 'limits.json', 
      label: 'Лимиты записей', 
      path: 'limits.json',
      type: 'limits'
    }
  ];

  // Дни недели для выбора
  const weekDays = [
    'Понедельник', 'Вторник', 'Среда', 
    'Четверг', 'Пятница', 'Суббота', 'Воскресенье'
  ];

  const loadFile = async (file) => {
    setLoading(true);
    setMessage('');
    setNewItem('');
    setNewGroup({ name: '', theoryDays: [], theoryDuration: 1.5, theoryTime: '18:00' });
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Токен отсутствует');
      }

      const res = await fetch(`/api/files/${file.name}`, {
        headers: { 
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || `Ошибка ${res.status}`);
      }
      
      const data = await res.json();
      setFileData(data);
      setSelectedFile(file);
      setMessage('Файл загружен');
    } catch (err) {
      console.error('Ошибка загрузки:', err);
      setMessage('Ошибка: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveFile = async (newData) => {
    setLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Токен отсутствует');
      }

      const res = await fetch(`/api/files/${selectedFile.name}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newData)
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || `Ошибка ${res.status}`);
      }
      
      setFileData(newData);
      setMessage('Файл успешно сохранён');
    } catch (err) {
      console.error('Ошибка сохранения:', err);
      setMessage('Ошибка: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Для hours.json
  const handleObjectChange = (field, value) => {
    const newData = { ...fileData, [field]: value };
    setFileData(newData);
  };

  // Для массивов (locations, times)
  const handleAddItem = () => {
    if (!newItem.trim()) {
      setMessage('Введите значение');
      return;
    }
    
    const newData = [...(fileData || []), newItem.trim()];
    setFileData(newData);
    setNewItem('');
    setMessage('Элемент добавлен (не забудьте сохранить)');
  };

  const handleRemoveItem = (index) => {
    const newData = fileData.filter((_, i) => i !== index);
    setFileData(newData);
    setMessage('Элемент удалён (не забудьте сохранить)');
  };

  // Для groups
  const handleAddGroup = () => {
    if (!newGroup.name.trim()) {
      setMessage('Введите название группы');
      return;
    }
    
    if (newGroup.theoryDays.length === 0) {
      setMessage('Выберите хотя бы один день');
      return;
    }
    
    const newId = fileData.length > 0 
      ? Math.max(...fileData.map(g => g.id)) + 1 
      : 1;
    
    const newData = [
      ...fileData,
      {
        id: newId,
        name: newGroup.name,
        theoryDays: [...newGroup.theoryDays],
        theoryDuration: newGroup.theoryDuration,
        theoryTime: newGroup.theoryTime
      }
    ];
    
    setFileData(newData);
    setNewGroup({ name: '', theoryDays: [], theoryDuration: 1.5, theoryTime: '18:00' });
    setMessage('Группа добавлена (не забудьте сохранить)');
  };

  const handleRemoveGroup = (id) => {
    const newData = fileData.filter(g => g.id !== id);
    setFileData(newData);
    setMessage('Группа удалена (не забудьте сохранить)');
  };

  const handleUpdateGroup = (id, field, value) => {
    const newData = fileData.map(g => 
      g.id === id ? { ...g, [field]: value } : g
    );
    setFileData(newData);
  };

  const handleAddDayToGroup = (groupId, day) => {
    if (!day) return;
    const newData = fileData.map(g => {
      if (g.id === groupId && !g.theoryDays.includes(day)) {
        return { ...g, theoryDays: [...g.theoryDays, day].sort() };
      }
      return g;
    });
    setFileData(newData);
  };

  const handleRemoveDayFromGroup = (groupId, day) => {
    const newData = fileData.map(g => {
      if (g.id === groupId) {
        return { ...g, theoryDays: g.theoryDays.filter(d => d !== day) };
      }
      return g;
    });
    setFileData(newData);
  };

  return (
    <div className="min-h-screen bg-[#1b1b1b] text-[#dedede]">
      <Header />
      
      <section className="py-24">
        <div className="max-w-screen-xl mx-auto px-8">
          <h1 className="text-5xl font-black uppercase tracking-tighter mb-12 text-center">
            Редактор файлов
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Список файлов */}
            <div className="bg-[#2a2a2a] rounded-2xl p-6">
              <h2 className="text-xl font-black mb-4">Файлы</h2>
              <div className="space-y-2">
                {files.map(file => (
                  <button
                    key={file.name}
                    onClick={() => loadFile(file)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      selectedFile?.name === file.name 
                        ? 'bg-primary text-black' 
                        : 'bg-[#333333] hover:bg-[#3a3a3a]'
                    }`}
                  >
                    <div className="font-medium">{file.label}</div>
                    <div className="text-xs opacity-70 mt-1">
                      {file.path}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Редактор */}
            <div className="md:col-span-2 bg-[#2a2a2a] rounded-2xl p-6">
              {selectedFile ? (
                <>
                  <h2 className="text-xl font-black mb-4">
                    Редактирование: {selectedFile.label}
                  </h2>
                  
                  {loading && <p className="text-center py-4">Загрузка...</p>}
                  
                  {!loading && fileData && (
                    <>
                      {/* hours.json */}
                      {selectedFile.type === 'object' && (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm text-slate-400 mb-2">
                              Теория (часы)
                            </label>
                            <input
                              type="number"
                              value={fileData.theory || 0}
                              onChange={(e) => handleObjectChange('theory', parseInt(e.target.value) || 0)}
                              className="w-full bg-[#1e1e1e] text-[#dedede] p-3 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-slate-400 mb-2">
                              Практика (занятия)
                            </label>
                            <input
                              type="number"
                              value={fileData.practice || 0}
                              onChange={(e) => handleObjectChange('practice', parseInt(e.target.value) || 0)}
                              className="w-full bg-[#1e1e1e] text-[#dedede] p-3 rounded-lg"
                            />
                          </div>
                          <button
                            onClick={() => saveFile(fileData)}
                            className="px-6 py-3 bg-primary text-black font-bold rounded-lg hover:bg-primary/80 transition-colors"
                          >
                            Сохранить
                          </button>
                        </div>
                      )}

                      {/* locations.json / times.json */}
                      {selectedFile.type === 'array' && (
                        <div className="space-y-6">
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                            {fileData.map((item, index) => (
                              <div key={index} className="flex items-center gap-2 bg-[#1e1e1e] p-3 rounded-lg">
                                <span className="flex-1">{item}</span>
                                <button
                                  onClick={() => handleRemoveItem(index)}
                                  className="p-2 bg-red-600/20 text-red-400 rounded hover:bg-red-600/40"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            ))}
                          </div>
                          
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={newItem}
                              onChange={(e) => setNewItem(e.target.value)}
                              placeholder={`Введите ${selectedFile.itemLabel}`}
                              className="flex-1 bg-[#1e1e1e] text-[#dedede] p-3 rounded-lg"
                            />
                            <button
                              onClick={handleAddItem}
                              className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
                            >
                              <Plus size={20} />
                            </button>
                          </div>
                          
                          <button
                            onClick={() => saveFile(fileData)}
                            className="w-full px-6 py-3 bg-primary text-black font-bold rounded-lg hover:bg-primary/80"
                          >
                            Сохранить
                          </button>
                        </div>
                      )}

                      {/* groups.json */}
                      {selectedFile.type === 'groups' && (
                        <div className="space-y-8">
                          {/* Существующие группы */}
                          <div className="space-y-4">
                            <h3 className="text-lg font-bold">Существующие группы</h3>
                            {fileData.map((group) => (
                              <div key={group.id} className="bg-[#1e1e1e] p-4 rounded-lg space-y-3">
                                <div className="flex justify-between items-center">
                                  <span className="font-bold text-primary">#{group.id}</span>
                                  <button
                                    onClick={() => handleRemoveGroup(group.id)}
                                    className="p-2 bg-red-600/20 text-red-400 rounded hover:bg-red-600/40"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                                
                                <div>
                                  <label className="text-xs text-slate-400">Название группы</label>
                                  <input
                                    type="text"
                                    value={group.name}
                                    onChange={(e) => handleUpdateGroup(group.id, 'name', e.target.value)}
                                    className="w-full bg-black/30 text-[#dedede] p-2 rounded-lg mt-1"
                                    disabled
                                  />
                                  <p className="text-xs text-slate-500 mt-1">Название нельзя изменить</p>
                                </div>
                                
                                <div>
                                  <label className="text-xs text-slate-400">Дни занятий</label>
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {group.theoryDays.map(day => (
                                      <span key={day} className="flex items-center gap-1 bg-primary/20 text-primary px-3 py-1 rounded-full text-sm">
                                        {day}
                                        <button
                                          onClick={() => handleRemoveDayFromGroup(group.id, day)}
                                          className="hover:text-red-400"
                                        >
                                          <X size={14} />
                                        </button>
                                      </span>
                                    ))}
                                  </div>
                                  <div className="flex gap-2 mt-2">
                                    <select
                                      value=""
                                      onChange={(e) => {
                                        handleAddDayToGroup(group.id, e.target.value);
                                        e.target.value = '';
                                      }}
                                      className="flex-1 bg-black/30 text-[#dedede] p-2 rounded-lg"
                                    >
                                      <option value="">Добавить день</option>
                                      {weekDays.map(day => (
                                        <option key={day} value={day}>{day}</option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="text-xs text-slate-400">Длительность (часы)</label>
                                    <input
                                      type="number"
                                      step="0.5"
                                      min="0.5"
                                      max="4"
                                      value={group.theoryDuration}
                                      onChange={(e) => handleUpdateGroup(group.id, 'theoryDuration', parseFloat(e.target.value))}
                                      className="w-full bg-black/30 text-[#dedede] p-2 rounded-lg mt-1"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-slate-400">Время начала</label>
                                    <input
                                      type="time"
                                      value={group.theoryTime}
                                      onChange={(e) => handleUpdateGroup(group.id, 'theoryTime', e.target.value)}
                                      className="w-full bg-black/30 text-[#dedede] p-2 rounded-lg mt-1"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Добавление новой группы */}
                          <div className="bg-[#1e1e1e] p-4 rounded-lg space-y-3">
                            <h3 className="text-lg font-bold">Новая группа</h3>
                            
                            <div>
                              <label className="text-xs text-slate-400">Название группы</label>
                              <input
                                type="text"
                                value={newGroup.name}
                                onChange={(e) => setNewGroup({...newGroup, name: e.target.value})}
                                placeholder="Группа 4"
                                className="w-full bg-black/30 text-[#dedede] p-2 rounded-lg mt-1"
                              />
                              <p className="text-xs text-slate-500 mt-1">Будет создан автоматический ID</p>
                            </div>
                            
                            <div>
                              <label className="text-xs text-slate-400">Дни занятий</label>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {newGroup.theoryDays.map(day => (
                                  <span key={day} className="flex items-center gap-1 bg-primary/20 text-primary px-3 py-1 rounded-full text-sm">
                                    {day}
                                    <button
                                      onClick={() => setNewGroup({
                                        ...newGroup, 
                                        theoryDays: newGroup.theoryDays.filter(d => d !== day)
                                      })}
                                      className="hover:text-red-400"
                                    >
                                      <X size={14} />
                                    </button>
                                  </span>
                                ))}
                              </div>
                              <div className="flex gap-2 mt-2">
                                <select
                                  value={selectedDay}
                                  onChange={(e) => setSelectedDay(e.target.value)}
                                  className="flex-1 bg-black/30 text-[#dedede] p-2 rounded-lg"
                                >
                                  <option value="">Выберите день</option>
                                  {weekDays.map(day => (
                                    <option key={day} value={day}>{day}</option>
                                  ))}
                                </select>
                                <button
                                  onClick={() => {
                                    if (selectedDay && !newGroup.theoryDays.includes(selectedDay)) {
                                      setNewGroup({
                                        ...newGroup,
                                        theoryDays: [...newGroup.theoryDays, selectedDay].sort()
                                      });
                                      setSelectedDay('');
                                    }
                                  }}
                                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                                >
                                  <Plus size={20} />
                                </button>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs text-slate-400">Длительность (часы)</label>
                                <input
                                  type="number"
                                  step="0.5"
                                  min="0.5"
                                  max="4"
                                  value={newGroup.theoryDuration}
                                  onChange={(e) => setNewGroup({...newGroup, theoryDuration: parseFloat(e.target.value)})}
                                  className="w-full bg-black/30 text-[#dedede] p-2 rounded-lg mt-1"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-slate-400">Время начала</label>
                                <input
                                  type="time"
                                  value={newGroup.theoryTime}
                                  onChange={(e) => setNewGroup({...newGroup, theoryTime: e.target.value})}
                                  className="w-full bg-black/30 text-[#dedede] p-2 rounded-lg mt-1"
                                />
                              </div>
                            </div>
                            
                            <button
                              onClick={handleAddGroup}
                              className="w-full px-6 py-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                            >
                              <Plus size={20} />
                              Добавить группу
                            </button>
                          </div>

                          <button
                            onClick={() => saveFile(fileData)}
                            className="w-full px-6 py-3 bg-primary text-black font-bold rounded-lg hover:bg-primary/80 flex items-center justify-center gap-2"
                          >
                            <Save size={20} />
                            Сохранить все группы
                          </button>
                        </div>
                      )}

                      {/* limits.json */}
                      {selectedFile.type === 'limits' && (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm text-slate-400 mb-2">
                              Недельный лимит записей
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="20"
                              value={fileData.weeklyBookingLimit || 3}
                              onChange={(e) => setFileData({...fileData, weeklyBookingLimit: parseInt(e.target.value) || 1})}
                              className="w-full bg-[#1e1e1e] text-[#dedede] p-3 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-slate-400 mb-2">
                              Сообщение при лимите
                            </label>
                            <textarea
                              value={fileData.message || ''}
                              onChange={(e) => setFileData({...fileData, message: e.target.value})}
                              rows="3"
                              className="w-full bg-[#1e1e1e] text-[#dedede] p-3 rounded-lg"
                            />
                          </div>
                          <button
                            onClick={() => saveFile(fileData)}
                            className="px-6 py-3 bg-primary text-black font-bold rounded-lg hover:bg-primary/80"
                          >
                            Сохранить
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {message && (
                    <p className={`mt-4 ${message.includes('Ошибка') ? 'text-red-500' : 'text-green-500'}`}>
                      {message}
                    </p>
                  )}
                </>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-500">
                  Выберите файл для редактирования
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <BottomNav />
    </div>
  );
}
